import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card, { CardProps } from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { SvgIconProps } from '@mui/material/SvgIcon';
import FilterListIcon from '@mui/icons-material/FilterList';
import DataTable, {
  DataTableWrapper,
  getActionsColConfig,
} from 'src/components/DataTable';
import { useStoreUnitActions } from 'src/redux/helpers';
import DataTableSearchBar from 'src/components/DataTable/components/DataTableSearchBar';
import { FilterTableContext } from 'src/components/DataTable/components/FilterTableContext';
import { ReactComponent as ErrorIcon } from 'src/assets/icons/error_icon.svg';
import { ReactComponent as GroupIcon } from 'src/assets/icons/Group_icon.svg';
import { ReactComponent as OperationIcon } from 'src/assets/icons/operations-icon.svg';
import { ReactComponent as OpenAlert } from 'src/assets/icons/Repo_icon.svg';
import { ReactComponent as WarningIcon } from 'src/assets/icons/warning_icon.svg';
import { ALERT_SEVERITY } from 'src/pages/Solutions/SolutionAlertRules/constant';
import { alertItem, AlertsListItemField } from '../types';
import alertsStore, { useAlertsList } from '../redux';
import AlertListAction from './AlertListAction';
import AlertSeverity from './AlertSeverity';

type Props = CardProps<any> & {
  title?: string;
  description?: string;
  Icon: React.JSXElementConstructor<SvgIconProps>;
};

const columns = [
  {
    id: AlertsListItemField.Number,
    title: 'Alert Number',
  },
  {
    id: AlertsListItemField.MetricName,
    title: 'Metric Name',
  },
  {
    id: AlertsListItemField.Severity,
    title: 'Severity',
    Component: AlertSeverity,
  },
  {
    id: AlertsListItemField.State,
    title: 'State',
  },
  {
    id: AlertsListItemField.AssignedTo,
    title: 'Assigned To',
  },
  {
    id: AlertsListItemField.CreatedDateTime,
    title: 'Created',
  },
  {
    id: AlertsListItemField.UpdatedDateTime,
    title: 'Updated',
  },
  getActionsColConfig(AlertListAction),
];

const filteredColumns = [
  {
    id: AlertsListItemField.Number,
    title: 'Alert Number',
  },
  {
    id: AlertsListItemField.MetricName,
    title: 'Metric Name',
  },
  {
    id: AlertsListItemField.Severity,
    title: 'Severity',
  },
];

const filterMenu = {
  severity: ALERT_SEVERITY,
};

function AlertCardList() {
  const [data, loading] = useAlertsList();
  const actions = useStoreUnitActions(alertsStore);
  const [showFilters, setShowFilter] = useState(false);
  const [filterString, setFilterString] = useState('');
  const [filterData, setFilterData] = useState<alertItem[]>([]);
  const [state, setState] = useState({ open: 0, reOpen: 0, flapping: 0 });
  const [severity, setSeverity] = useState({ critical: 0, minor: 0 });
  const rowData = data;

  useEffect(() => {
    actions.alertsList();
  }, []);

  useEffect(() => {
    if (!data) return;
    const openAlerts = data.filter((alert) => alert.state === 'Open').length;
    const reopenAlerts = data.filter(
      (alert) => alert.state === 'Reopened'
    ).length;
    const flappingAlerts = data.filter(
      (alert) => alert.state === 'Flapping'
    ).length;
    const criticalAlerts = data.filter(
      (alert) => alert.severity === 'Critical'
    ).length;
    const minorAlerts = data.filter(
      (alert) => alert.severity === 'Minor'
    ).length;

    setState((prev) => {
      if (
        prev.open === openAlerts &&
        prev.reOpen === reopenAlerts &&
        prev.flapping === flappingAlerts
      ) {
        return prev;
      }
      return {
        open: openAlerts,
        reOpen: reopenAlerts,
        flapping: flappingAlerts,
      };
    });

    setSeverity((prev) => {
      if (prev.critical === criticalAlerts && prev.minor === minorAlerts) {
        return prev;
      }
      return {
        critical: criticalAlerts,
        minor: minorAlerts,
      };
    });
  }, [data]);

  const cardData = [
    { title: 'Open Alerts', description: state.open, icon: OpenAlert },
    {
      title: 'Critical Alerts',
      description: severity.critical,
      icon: WarningIcon,
    },
    { title: 'Minor Alerts', description: severity.minor, icon: ErrorIcon },
    { title: 'Reopen Alerts', description: state.reOpen, icon: GroupIcon },
    {
      title: 'Flapping Alerts',
      description: state.flapping,
      icon: OperationIcon,
    },
  ];

  const handleClick = (event) => {
    event.preventDefault();
    setShowFilter((prevs) => !prevs);
  };

  function toggleshowfilter() {
    setShowFilter((prevs) => !prevs);
  }
  function scrollerUpdateWithFilter(filterstrings) {
    setFilterString(filterstrings);
  }

  const filterContextValue = React.useMemo(
    () => ({
      showFilters,
      toggleshowfilter,
      scrollerUpdateWithFilter,
      filterData,
      setFilterData,
      rowData,
    }),
    [showFilters, filterData, rowData]
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: 2,
          mb: 2,
        }}
      >
        {cardData?.map((card) => (
          <Card
            key={card.title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: 300,
              minHeight: 90,
              px: 2,
              py: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <card.icon style={{ width: 32, height: 32 }} />
            </Box>
            <Box>
              <Typography fontSize="14px" sx={{ color: '#1E1E1E80' }}>
                {card.title}
              </Typography>
              <Typography
                sx={{ fontWeight: 500, fontSize: '26px', color: '#434343' }}
              >
                {card.description}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
      <FilterTableContext.Provider value={filterContextValue}>
        <DataTableWrapper
          searchBar={false}
          actionsPanel={
            <Box>
              <Button
                onClick={handleClick}
                sx={{
                  padding: '8px',
                  marginRight: '7px',
                  marginLeft: '-8px',
                }}
                data-testid="filter-button"
              >
                <FilterListIcon />
              </Button>
              <DataTableSearchBar style={{ paddingRight: '10px' }} />
            </Box>
          }
        >
          <DataTable
            data={filterString ? filterData : data}
            loading={loading}
            columns={columns}
            filteredColumns={filteredColumns}
            filterMenus={filterMenu}
            columnTypes={{
              number: 'textfield',
              metricName: 'textfield',
              severity: 'select',
            }}
            actionName={actions.alertsList}
          />
        </DataTableWrapper>
      </FilterTableContext.Provider>
    </>
  );
}

export default AlertCardList;
